import { PaletteOptions } from '@mui/material'
import { COLORS } from './colors'
// Imported from leaf modules rather than the `@/components` barrel: the barrel pulls in
// components that import `@/styles`, creating a cycle that breaks at runtime.
import { buttonColors } from '@/components/Inputs/Button/Button.style'
import type { ButtonColors } from '@/components/Inputs/Button/Button.types'
import { spinnerColors } from '@/components/Feedback/Spinner/Spinner.style'
import type { SpinnerColors } from '@/components/Feedback/Spinner/Spinner.types'

export const palette: PaletteOptions = {
  primary: {
    main: COLORS.PRIMARY,
    light: COLORS.PRIMARY_LIGHT,
    dark: COLORS.PRIMARY_DARK,
    smokyBlack: COLORS.SMOKY_BLACK,
  },
  background: {
    default: COLORS.WHITE,
    secondary: COLORS.LOTION,
    dark: COLORS.PRIMARY,
    primaryAlt: COLORS.ALICE_BLUE,
    smokyBlack: COLORS.SMOKY_BLACK,
  },
  foreground: {
    primary900: COLORS.PRIMARY_900,
    quinary400: COLORS.METALLIC_SILVER,
    quaternary500: COLORS.QUATERNARY_500,
  },
  border: {
    primary: COLORS.PLACEHOLDER_SUBTLE,
    secondary: COLORS.BRIGHT_GRAY,
    brand: COLORS.DODGER_BLUE,
  },
  button: {
    ...buttonColors,
  },
  spinner: {
    ...spinnerColors,
  },
  text: {
    primary900: COLORS.PRIMARY_900,
    secondary700: COLORS.SECONDARY_700,
    tertiary600: COLORS.TERTIARY_600,
    primaryContrast: COLORS.WHITE,
    primaryWarmIvory: COLORS.WARM_IVORY,
    primarySmokyBlack: COLORS.SMOKY_BLACK,
    primary: COLORS.PRIMARY,
    primaryLight: COLORS.PRIMARY_LIGHT,
    hyperLink: COLORS.HYPER_LINK,
    placeHolder: COLORS.PALE_SKY,
    placeHolderSubtle: COLORS.PLACEHOLDER_SUBTLE,
    quaternary500: COLORS.QUATERNARY_500,
    brandSecondary700: COLORS.HYPER_LINK,
  },
  action: {
    disabled: COLORS.FOREGROUND_DISABLED,
  },
  error: {
    main: COLORS.ERROR_MAIN,
    dark: COLORS.ERROR_DARK,
  },
  utils: {
    gray50: COLORS.LOTION,
    gray200: COLORS.BRIGHT_GRAY,
    gray300: COLORS.LIGHT_SILVER,
    gray400: COLORS.SPANISH_GRAY,
    gray500: COLORS.PALE_SKY,
    gray600: COLORS.GRANITE_GRAY,
    gray700: COLORS.MAKO,
    gray800: COLORS.EBONY_CLAY,
    gray900: COLORS.CHARCOAL_GRAY,
    gray950: COLORS.RICH_BLACK,

    grayBlue50: COLORS.WHITE_LILAC,
    grayBlue200: COLORS.PERIWINKLE_GRAY,
    grayBlue500: COLORS.LIBERTY,
    grayBlue700: COLORS.AMERICAN_BLUE,

    grayLight100: COLORS.WILD_SAND,

    success50: COLORS.HONEY_DEW,
    success200: COLORS.PALE_GREEN,
    success500: COLORS.GREEN_CRAYOLA,
    success700: COLORS.DARTMOUTH_GREEN,

    blue200: COLORS.PALE_BLUE,
    blue300: COLORS.SKY_BLUE,

    green600: COLORS.SPRING_GREEN,

    star: COLORS.VIVID_ORANGE,
  },
  brand: {
    primary100: COLORS.WATER_BLUE,
    primary600: COLORS.AZURE_SKY,
    primary700: COLORS.HYPER_LINK,
    utility50: COLORS.ALICE_BLUE,
  },
  pageTheme: {
    primaryColor: COLORS.PRIMARY,
    secondaryColor: COLORS.WARM_IVORY,
    textColor: COLORS.WHITE,
    mapHighlightColor: COLORS.AZURE_SKY,
  },
}

export interface BackgroundColors {
  default: string
  secondary: string
  dark: string
  primaryAlt: string
  smokyBlack: string
}

interface BrandColors {
  primary100: string
  primary600: string
  primary700: string
  utility50: string
}

interface PageThemeColors {
  primaryColor: string
  secondaryColor: string
  textColor: string
  mapHighlightColor: string
}

interface ForegroundColors {
  primary900: string
  quinary400: string
  quaternary500: string
}

interface BorderColors {
  primary: string
  secondary: string
  brand: string
}

interface UtilColors {
  gray50: string
  gray200: string
  gray300: string
  gray400: string
  gray500: string
  gray600: string
  gray700: string
  gray800: string
  gray900: string
  gray950: string

  grayBlue50: string
  grayBlue200: string
  grayBlue500: string
  grayBlue700: string

  grayLight100: string

  success50: string
  success200: string
  success500: string
  success700: string

  blue200: string
  blue300: string

  green600: string

  star: string
}

export interface CustomColors {
  brand: BrandColors
  foreground: ForegroundColors
  border: BorderColors
  button: ButtonColors
  pageTheme: PageThemeColors
  spinner: SpinnerColors
  utils: UtilColors
}
